import React, { createContext, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [language, setLanguage] = useState('en');
  const [isRTL, setIsRTL] = useState(false);
  const [loading, setLoading] = useState(false);

  // Initialize language from user profile or localStorage
  useEffect(() => {
    const initializeLanguage = () => {
      // Priority: user profile > localStorage > default
      const userLanguage = user?.language;
      const savedLanguage = localStorage.getItem('i18nextLng');
      const initialLanguage = userLanguage || savedLanguage || 'en';

      if (initialLanguage !== language) {
        setLanguage(initialLanguage);
        i18n.changeLanguage(initialLanguage);
        updateDirection(initialLanguage);
      }
    };

    initializeLanguage();
  }, [user]); // Re-run when user changes (login/logout)

  // Update document direction based on language
  const updateDirection = (lang) => {
    const rtl = lang === 'he';
    setIsRTL(rtl);
    // Keep layout as LTR for both languages - only translate text
    document.documentElement.setAttribute('dir', 'ltr');
    document.documentElement.setAttribute('lang', lang);
  };

  // Change language and sync with backend
  const changeLanguage = async (newLanguage) => {
    if (newLanguage === language) return;

    setLoading(true);
    try {
      // Update i18next
      await i18n.changeLanguage(newLanguage);

      // Update state
      setLanguage(newLanguage);
      updateDirection(newLanguage);

      // Save to localStorage
      localStorage.setItem('i18nextLng', newLanguage);

      // Sync with backend if user is logged in
      if (user?.id) {
        const token = localStorage.getItem('userToken');
        if (token) {
          const response = await fetch(`/api/users/${user.id}/preferences/language`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ language: newLanguage })
          });

          if (!response.ok) {
            console.error('Failed to sync language preference with backend');
            // Don't throw - local language change still works
          }
        }
      }
    } catch (error) {
      console.error('Error changing language:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    language,
    changeLanguage,
    isRTL,
    loading
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
